import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
interface SlideAtmosphereProps {
  imageUrl?: string;
  videoUrl?: string;
  opacity?: number;
  overlayGradient?: 'dark' | 'royal' | 'gold' | 'none';
  withVignette?: boolean;
  withTexture?: boolean;
  withParticles?: boolean;
  animate?: 'ken-burns' | 'fade' | 'none';
  playbackRate?: number;
  videoBrightness?: number;
  lightScrim?: boolean;
}
export const SlideAtmosphere: React.FC<SlideAtmosphereProps> = ({
  imageUrl,
  videoUrl,
  opacity = 0.4,
  overlayGradient = 'dark',
  withVignette = true,
  withTexture = true,
  withParticles = false,
  animate = 'ken-burns',
  playbackRate = 1,
  videoBrightness = 1,
  lightScrim = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, videoUrl]);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1163">
      {/* Base Background */}
      <div className="absolute inset-0 bg-black" data-id="element-1164" />

      {/* Media Layer */}
      {imageUrl && <motion.div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url('${imageUrl}')`,
      opacity
    }} initial={animate === 'ken-burns' ? {
      scale: 1.1,
      opacity: 0
    } : animate === 'fade' ? {
      opacity: 0
    } : {}} whileInView={animate === 'ken-burns' ? {
      scale: 1,
      opacity
    } : animate === 'fade' ? {
      opacity
    } : {}} transition={{
      duration: 2,
      ease: 'easeOut'
    }} viewport={{
      once: true
    }} data-id="element-1165" />}
      {videoUrl && <motion.div className="absolute inset-0" style={{
      opacity
    }} initial={animate === 'fade' ? {
      opacity: 0
    } : {}} whileInView={animate === 'fade' ? {
      opacity
    } : {}} transition={{
      duration: 1.5,
      ease: 'easeOut'
    }} viewport={{
      once: true
    }} data-id="element-1166">
          <video ref={videoRef} src={videoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" style={{
        filter: `brightness(${videoBrightness})`
      }} data-id="element-1167" />
        </motion.div>}

      {/* Gradients */}
      {overlayGradient === 'dark' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" data-id="element-1168" />}
      {overlayGradient === 'royal' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-[#0a0604]/90 via-[#1B2A4A]/40 to-black/90 mix-blend-multiply" data-id="element-1169" />}
      {overlayGradient === 'gold' && !lightScrim && <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-[#D4AF37]/10 to-black/90" data-id="element-1170" />}

      {/* Light Scrim — keeps video bright, just a soft top/bottom darkening + center readability band */}
      {lightScrim && <>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" data-id="element-1171" />
          <div className="absolute inset-0 bg-black/15" data-id="element-1172" />
        </>}

      {/* Vignette */}
      {withVignette && <div className={`absolute inset-0 ${lightScrim ? 'bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]' : 'bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]'}`} data-id="element-1173" />}

      {/* Archival Texture */}
      {withTexture && <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: `url('https://www.transparenttextures.com/patterns/old-wall.png')`
    }} data-id="element-1174" />}

      {/* Particles / Dust */}
      {withParticles && <motion.div className="absolute inset-0 opacity-30" style={{
      backgroundImage: `url('https://www.transparenttextures.com/patterns/stardust.png')`
    }} animate={{
      y: [0, -20, 0],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1175" />}
    </div>;
};